import jsonpointer from 'json-pointer';
import { DateTime } from 'luxon';

import { SelectOption } from './components/Select';
import {
  APIFieldError,
  BaseResource,
  Choice,
  FieldObjectV3,
  LinkChoiceV3,
  LinkObjectV3,
} from './rest/types';

export type BaseField<V, E> = {
  readonly name: string;
  readonly label: string;
  readonly set: (resource: BaseResource, newValue: V) => void;
  readonly getErrors: (resource: BaseResource, errors: ReadonlyArray<APIFieldError>) => E;
  readonly defaultValue: V;
};

export type StringField = BaseField<string, ReadonlyArray<string>> & {
  readonly type: 'string';
  readonly maxLength?: number;
  readonly suggestions?: ReadonlyArray<string>;
};

export type DateField = BaseField<DateTime | null, ReadonlyArray<string>> & {
  readonly type: 'date';
  readonly allowNull: boolean;
};

export type DateTimeField = BaseField<DateTime | null, ReadonlyArray<string>> & {
  readonly type: 'datetime';
  readonly allowNull: boolean;
};

export type ChoiceField = BaseField<string | number | null, ReadonlyArray<string>> & {
  readonly type: 'choice';
  readonly allowEmpty: boolean;
  readonly emptyValue: string | number | null;
  readonly choices: ReadonlyArray<SelectOption>;
};

export type FormField = StringField | DateField | DateTimeField | ChoiceField;

const getFieldErrors = (
  errors: ReadonlyArray<APIFieldError>,
  fieldPointer: string,
): ReadonlyArray<string> =>
  errors.filter((error) => error.source.pointer === fieldPointer).map((error) => error.detail);

const makeFieldName = (group: string, name: string): string =>
  // NOTE: We have to replace dots in the field names, so they do not lead to nested fields in the form data.
  `${group}__${name.replaceAll('.', '__')}`;

export const makeStringField = (
  fieldPointer: string,
  {
    group,
    name,
    label,
    maxLength,
    defaultValue,
    suggestions,
  }: {
    group: string;
    name: string;
    label: string;
    maxLength?: number;
    defaultValue?: string;
    suggestions?: ReadonlyArray<string>;
  },
): StringField => ({
  type: 'string',
  name: makeFieldName(group, name),
  label,
  maxLength,
  suggestions,
  set: (resource, value) => jsonpointer.set(resource, fieldPointer, value),
  getErrors: (_resource, errors) => getFieldErrors(errors, fieldPointer),
  defaultValue: defaultValue || '',
});

export const makeDateField = (
  fieldPointer: string,
  {
    group,
    name,
    label,
    allowNull = false,
    defaultValue,
  }: { group: string; name: string; label: string; allowNull?: boolean; defaultValue?: string },
): DateField => ({
  type: 'date',
  name: makeFieldName(group, name),
  label,
  allowNull,
  set: (resource, value) =>
    jsonpointer.set(resource, fieldPointer, value?.toFormat('yyyy-MM-dd') || null),
  getErrors: (_resource, errors) => getFieldErrors(errors, fieldPointer),
  defaultValue: defaultValue ? DateTime.fromFormat(defaultValue, 'yyyy-MM-dd') : null,
});

export const makeDateTimeField = (
  fieldPointer: string,
  {
    group,
    name,
    label,
    allowNull = false,
    defaultValue,
  }: { group: string; name: string; label: string; allowNull?: boolean; defaultValue?: string },
): DateTimeField => ({
  type: 'datetime',
  name: makeFieldName(group, name),
  label,
  allowNull,
  set: (resource, value) => jsonpointer.set(resource, fieldPointer, value?.toISO() || null),
  getErrors: (_resource, errors) => getFieldErrors(errors, fieldPointer),
  defaultValue: defaultValue ? DateTime.fromISO(defaultValue) : null,
});

export const makeChoiceField = (
  fieldPointer: string,
  {
    group,
    name,
    label,
    allowEmpty = false,
    emptyValue = null,
    defaultValue,
    choices,
  }: {
    group: string;
    name: string;
    label: string;
    allowEmpty?: boolean;
    emptyValue: string | number | null;
    defaultValue?: string;
    choices: ReadonlyArray<Choice<string | number>>;
  },
): ChoiceField => ({
  type: 'choice',
  name: makeFieldName(group, name),
  label,
  allowEmpty,
  emptyValue,
  choices: choices.map(({ value, display_name }) => ({
    value,
    display: display_name,
  })),
  set: (resource, value) => jsonpointer.set(resource, fieldPointer, value),
  getErrors: (_resource, errors) => getFieldErrors(errors, fieldPointer),
  defaultValue: defaultValue || emptyValue,
});

export const fieldObjectToFormField = (
  pointer: string,
  group: string,
  name: string,
  {
    type,
    label,
    max_length: maxLength,
    allow_null,
    allow_blank,
    choices,
    suggestions,
    initial: defaultValue,
  }: Pick<
    FieldObjectV3,
    | 'type'
    | 'label'
    | 'max_length'
    | 'allow_null'
    | 'allow_blank'
    | 'choices'
    | 'suggestions'
    | 'initial'
  >,
): FormField | null => {
  const fieldPointer = `${pointer}/${jsonpointer.escape(name)}`;
  switch (type) {
    case 'string': {
      return makeStringField(fieldPointer, {
        group,
        name,
        label,
        maxLength,
        defaultValue,
        suggestions,
      });
    }
    case 'date': {
      return makeDateField(fieldPointer, {
        group,
        name,
        label,
        allowNull: allow_null,
        defaultValue,
      });
    }
    case 'datetime': {
      return makeDateTimeField(fieldPointer, {
        group,
        name,
        label,
        allowNull: allow_null,
        defaultValue,
      });
    }
    case 'choice': {
      return makeChoiceField(fieldPointer, {
        group,
        name,
        label,
        allowEmpty: allow_null || allow_blank,
        emptyValue: allow_null ? null : '',
        defaultValue,
        choices: choices || [],
      });
    }
    default:
      return null;
  }
};

export const makeLinkChoiceField = (
  fieldPointer: string,
  {
    group,
    name,
    label,
    allowNull = false,
    defaultValue,
    choices,
  }: {
    group: string;
    name: string;
    label: string;
    allowNull?: boolean;
    defaultValue?: string;
    choices: ReadonlyArray<LinkChoiceV3>;
  },
): ChoiceField => ({
  type: 'choice',
  name: makeFieldName(group, name),
  label,
  allowEmpty: allowNull,
  emptyValue: null,
  choices: choices?.map(({ href, title }) => ({ value: href, display: title })) || [],
  set: (resource, value) => jsonpointer.set(resource, fieldPointer, value),
  getErrors: (
    _resource: BaseResource,
    errors: ReadonlyArray<APIFieldError>,
  ): ReadonlyArray<string> => getFieldErrors(errors, fieldPointer),
  defaultValue: defaultValue || null,
});

export const linkObjectToFormField = (
  pointer: string,
  group: string,
  name: string,
  {
    label,
    allow_null: allowNull,
    choices,
    initial: defaultValue,
  }: Pick<LinkObjectV3, 'label' | 'allow_null' | 'choices' | 'initial'>,
): ChoiceField =>
  makeLinkChoiceField(`${pointer}/_links/${jsonpointer.escape(name)}/href`, {
    group,
    name,
    label,
    allowNull,
    choices: choices || [],
    defaultValue,
  });
